import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Typography } from '@mui/material';
import { LOADING_STATES } from '../../../../utils/constants/loadingStates';
import { formatDurationBetweenTimestamps } from '../../../../utils/formatting/timeFormatting';
import { getBreakSubtitleData, getBreakStatusDisplayData } from '../../../../utils/helpers/breaks/breakHelpers';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';
import { selectAllBreaksByShiftId, setBreaks } from '../../../../redux/breaks/breaksSlice';
import { listenToBreaks } from '../../../../redux/breaks/breaksService';
import PropTypes from 'prop-types';

import {
  BreakTitleAndStatusContainer,
  BreaksContainer,
  BreakContainer,
  MessageContainer,
  BreakTimesWrapper,
} from './BreakTimes.styles';
import { StatusText } from '../../../shared/SharedStyles';

const BreakTimes = ({ shift, disabled = false }) => {
  const { formatTime, formatTimeRange } = useTimeFormatters();
  const dispatch = useDispatch();
  const updatedStatus = useSelector((state) => state.shifts.updatedStatus);

  // Derived state
  const isLoading = !shift || updatedStatus === LOADING_STATES.LOADING;
  const breaks = useSelector(selectAllBreaksByShiftId);
  const sortedBreaks =
    breaks.length > 0 ? [...breaks].sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart)) : [];

  useEffect(() => {
    if (shift.id) {
      // Set up Firestore real-time listener for breaks
      const unsubscribe = listenToBreaks(shift.id, (breaks) => {
        dispatch(setBreaks(breaks));
      });
      return () => unsubscribe();
    }
  }, [shift.id, dispatch]);

  // Animation variants for fade-in/out
  const fadeVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25 } },
    exit: { opacity: 0, transition: { duration: 0.25 } },
  };

  return (
    <BreakTimesWrapper>
      {!isLoading &&
        (breaks.length === 0 ? (
          <MessageContainer
            as={motion.div}
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ opacity: disabled ? 0.25 : 1 }}
          >
            <Typography variant="bodyRegular">No breaks available for this shift</Typography>
          </MessageContainer>
        ) : (
          <BreaksContainer
            as={motion.div}
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ opacity: disabled ? 0.25 : 1 }}
          >
            {sortedBreaks.map((breakData, index) => {
              const { breakSubtitle, breakStatus } = getBreakSubtitleData(breakData, formatTime, formatTimeRange);

              // Get break status display data
              const breakDisplayData = getBreakStatusDisplayData(breakData, formatDurationBetweenTimestamps);

              return (
                <BreakContainer
                  key={index}
                  as={motion.div}
                  variants={fadeVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{
                    cursor: disabled ? 'not-allowed' : 'default',
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                >
                  <BreakTitleAndStatusContainer>
                    <Typography variant="bodyRegular">{breakData.name}</Typography>
                    {breakSubtitle && (
                      <StatusText status={breakStatus} variant="subheadlineRegular">
                        {breakSubtitle}
                      </StatusText>
                    )}
                  </BreakTitleAndStatusContainer>

                  <StatusText status={breakDisplayData.status} variant="subheadlineRegular">
                    {breakDisplayData.text}
                  </StatusText>
                </BreakContainer>
              );
            })}
          </BreaksContainer>
        ))}
    </BreakTimesWrapper>
  );
};

BreakTimes.propTypes = {
  shift: PropTypes.object.isRequired,
  isCurrentlyAssigned: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default BreakTimes;
